// data/relics.ts — the in-run relic table (DESIGN §6).
// DATA ONLY: every entry is {hook: <registered key>, params}. The behaviour for
// each hook key lives in game/relics.ts. Nothing here executes.
//
// DESIGN §6 rule of record: "relics are verbs or economies, not bare +stats —
// at most 6 of 24 are flat numbers". Bare-stat count below: 3
// (deep_roots, whetstone, quickstep). Everything else changes a verb, an
// economy, or how a later zone's rule treats you.
//
// Rarity drives price (TUNING.economy.altarRelicCost[common|rare|epic]) and the
// offer weighting in game/relics.ts. Bosses offer richer tiers deeper (§6).

import type { RelicDef } from '../types'

export const RELICS: RelicDef[] = [
  // ------------------------------------------------------------- common (9)
  {
    id: 'deep_roots',
    name: 'Deep Roots',
    flavor: 'Something under the flagstones still remembers how to hold on.',
    desc: '+30 max HP.',
    rarity: 'common',
    hook: 'statMaxHp',
    params: { add: 30 },
  },
  {
    id: 'whetstone',
    name: 'Whetstone',
    flavor: 'A soldier who cannot sharpen is a soldier who cannot wait.',
    desc: '+15% damage.',
    rarity: 'common',
    hook: 'statDamage',
    params: { mul: 1.15 },
  },
  {
    id: 'quickstep',
    name: 'Quickstep',
    flavor: 'The dead keep time badly. Be somewhere else on the beat.',
    desc: '+8% move speed.',
    rarity: 'common',
    hook: 'statSpeed',
    params: { mul: 1.08 },
  },
  {
    id: 'ashfall',
    name: 'Ashfall',
    flavor: 'Cut the source and the source answers, once, loudly.',
    desc: 'Destroying a generator sets off a blast: 38 damage to everything nearby.',
    rarity: 'common',
    hook: 'onGeneratorKilled',
    params: { damage: 38, radius: 140 },
  },
  {
    id: 'wardens_ration',
    name: "Warden's Ration",
    flavor: 'Salt, fat and stubbornness. It goes further than it should.',
    desc: 'Food heals 60% more.',
    rarity: 'common',
    hook: 'foodHealMul',
    params: { mul: 1.6 },
  },
  {
    id: 'coin_reek',
    name: 'Coin-Reek',
    flavor: 'They swallowed their pay. You are only collecting arrears.',
    desc: 'Kills have a 14% chance to drop 5 gold.',
    rarity: 'common',
    hook: 'goldOnKill',
    params: { chance: 0.14, gold: 5 },
  },
  {
    id: 'quenchstone',
    name: 'Quenchstone',
    flavor: 'Cool iron on a burn. A room ends; you begin again a little whole.',
    desc: 'Heal 14 HP whenever a room is cleared.',
    rarity: 'common',
    hook: 'onRoomClear',
    params: { heal: 14 },
  },
  {
    id: 'ember_keeper',
    name: 'Ember-Keeper',
    flavor: 'Nothing that burned down here is allowed to go entirely out.',
    desc: '+20% embers from every source.',
    rarity: 'common',
    hook: 'emberBonus',
    params: { mul: 1.2 },
  },
  {
    id: 'tinder_wake',
    name: 'Tinder Wake',
    flavor: 'Run, and leave the floor arguing about it.',
    desc: 'Dashing leaves a burning trail (26 damage/sec for 3 seconds).',
    rarity: 'common',
    hook: 'dashBurnTrail',
    params: { dps: 26, seconds: 3.2, radius: 24 },
  },

  // --------------------------------------------------------------- rare (9)
  {
    id: 'gravity_of_gold',
    name: 'Gravity of Gold',
    flavor: 'Carry enough of it and the deep starts leaning your way.',
    desc: 'Gold and embers are pulled toward you. +1 damage per 100 gold held (max +8).',
    rarity: 'rare',
    hook: 'goldGravity',
    params: { radius: 190, perGold: 100, cap: 8 },
  },
  {
    id: 'last_ember',
    name: 'Last Ember',
    flavor: 'One refusal per descent. Spend it on the mistake you meant to make.',
    desc: 'Survive one lethal hit per zone, left at 1 HP.',
    rarity: 'rare',
    hook: 'lastEmber',
    params: { perZone: 1 },
  },
  {
    id: 'tidewalkers_soles',
    name: "Tidewalker's Soles",
    flavor: 'Cut from something that drowned and declined to notice.',
    desc: 'Water no longer slows you. +10% move speed on dry ground.',
    rarity: 'rare',
    hook: 'tidewalker',
    params: { dryMul: 1.1 },
  },
  {
    id: 'sextons_spade',
    name: "Sexton's Spade",
    flavor: 'The trick to burying the pale court is not stopping to look.',
    desc: 'Walking over a fallen husk seals it — no stopping to press E.',
    rarity: 'rare',
    hook: 'huskSpade',
    params: { walkSeal: 1 },
  },
  {
    id: 'moth_eaten_cloak',
    name: 'Moth-Eaten Cloak',
    flavor: 'The holes let the light out. The moths approve of that.',
    desc: '+30% lamp radius. Glintmoths ignore you.',
    rarity: 'rare',
    hook: 'mothCloak',
    params: { lampMul: 1.3 },
  },
  {
    id: 'appraisers_eye',
    name: "Appraiser's Eye",
    flavor: 'Every curse is a price tag read from the wrong side.',
    desc: 'Cursed treasure can no longer ambush you — only a 5-second mark.',
    rarity: 'rare',
    hook: 'appraiser',
    params: { markSeconds: 5 },
  },
  {
    id: 'keen_shaft',
    name: 'Keen Shaft',
    flavor: 'Two ranks, one hole. Line them up and be patient.',
    desc: 'Your shots pierce one extra enemy.',
    rarity: 'rare',
    hook: 'pierceAdd',
    params: { add: 1 },
  },
  {
    id: 'widows_flask',
    name: "Widow's Flask",
    flavor: 'She poured wider than the recipe. She had more to be angry at.',
    desc: 'Potion (Q) blast radius +45%.',
    rarity: 'rare',
    hook: 'potionRadius',
    params: { mul: 1.45 },
  },
  {
    id: 'gluttons_hearth',
    name: "Glutton's Hearth",
    flavor: 'Eat here often enough and the room starts feeding you back.',
    desc: 'Eating food also grants +3 max HP for the rest of the run.',
    rarity: 'rare',
    hook: 'onFoodEaten',
    params: { maxHpAdd: 3 },
  },

  // --------------------------------------------------------------- epic (6)
  {
    id: 'split_stride',
    name: 'Split Stride',
    flavor: 'Two steps of nothing at all, taken in the same breath.',
    desc: '+1 dash charge.',
    rarity: 'epic',
    hook: 'dashChargeAdd',
    params: { add: 1 },
  },
  {
    id: 'quickened_sigil',
    name: 'Quickened Sigil',
    flavor: 'Impatience, engraved. It insists the cooldown was always shorter.',
    desc: 'Special attack (RMB) cooldown −30%.',
    rarity: 'epic',
    hook: 'specialCooldownMul',
    params: { mul: 0.7 },
  },
  {
    id: 'sepulchre_tithe',
    name: 'Sepulchre Tithe',
    flavor: 'Seal one properly and the deep pays the burial fee.',
    desc: '+2 embers every time you seal a husk.',
    rarity: 'epic',
    hook: 'huskSealEmbers',
    params: { add: 2 },
  },
  {
    id: 'diviners_thread',
    name: "Diviner's Thread",
    flavor: 'It pulls, faintly, toward whatever the walls are hiding.',
    desc: 'Treasure that would bite you is marked with a ring.',
    rarity: 'epic',
    hook: 'treasureSense',
    params: { on: 1 },
  },
  {
    id: 'ashen_veil',
    name: 'Ashen Veil',
    flavor: 'The Gloom still comes. It simply has to find you first.',
    desc: 'The Gloom fills 15% slower.',
    rarity: 'epic',
    hook: 'gloomFillMul',
    params: { mul: 0.85 },
  },
  {
    id: 'hunters_mark',
    name: "Hunter's Mark",
    flavor: 'The big ones were carrying more than their share all along.',
    desc: 'Elite kills pay 2.5× embers.',
    rarity: 'epic',
    hook: 'eliteBounty',
    params: { mul: 2.5 },
  },
]

/** Rarity → index into TUNING.economy.altarRelicCost. */
export const RELIC_COST_INDEX: Record<RelicDef['rarity'], number> = {
  common: 0,
  rare: 1,
  epic: 2,
}

/** Offer weighting for a free/boss draw. Deeper zones tilt this in game/relics.ts. */
export const RELIC_RARITY_WEIGHT: Record<RelicDef['rarity'], number> = {
  common: 60,
  rare: 30,
  epic: 12,
}

/** Readability: rarity colour, used by the pick overlay. */
export const RELIC_RARITY_COLOR: Record<RelicDef['rarity'], string> = {
  common: '#cfc7ad',
  rare: '#6fc3ff',
  epic: '#c07bff',
}

/** Trivial table builder (permitted in data/): id → def. */
export const RELICS_BY_ID: Map<string, RelicDef> = new Map(RELICS.map((r) => [r.id, r]))
